import type { KrsEdge, KrsNode } from "../types/ast.js";
import { edgeKey, edgeKindKey } from "../view/edge-key.js";
import { bareIdView, nodePathRefId } from "../parser/node-path.js";
import { UNASSIGNED_SYSTEM_ID } from "../view/unassigned-system.js";
import type { DomainEdgeDetail, SystemFrameEdges, ViewSlice } from "../view/view-slice.js";

export type DiffState = "unchanged" | "added" | "removed" | "changed";

export interface NodeDiffMeta {
  state: DiffState;
  changes?: {
    label?: { before?: string; after?: string };
    annotations?: { added: string[]; removed: string[] };
    description?: { before?: string; after?: string };
  };
}

export interface EdgeDiffMeta {
  state: DiffState;
  changes?: {
    /**
     * Set when both sides had an aggregated implicit edge between the same
     * service pair but the underlying constituent domain edges differ.
     */
    domainEdges?: {
      added: DomainEdgeDetail[];
      removed: DomainEdgeDetail[];
    };
  };
}

export interface DiffedView {
  /** Union ViewSlice — passed to the existing layout/render pipeline. */
  slice: ViewSlice;
  /**
   * Diff state keyed by node id. Includes ghost users, systems and the canvas
   * cards. Where cards in two system frames share an id, the entry is the one
   * `bareIdView` picks from {@link nodesByPath} (ADR-2934).
   */
  nodes: Map<string, NodeDiffMeta>;
  /**
   * The canvas cards' diff state keyed by the full path the layout stamps on
   * each card (`LayoutNode.path`, in `nodePathRefId` form), for every system
   * frame on the multi-system root (#3126). A card reads its own state here, so
   * `Admin.Api` no longer shows the state `nodes` holds for `Shop.Api` (#2934).
   * Ghosts and system frames carry no path and stay in `nodes` only.
   */
  nodesByPath: Map<string, NodeDiffMeta>;
  /**
   * Diff state keyed by {@link edgeKey}.
   * Matches the LayoutEdge identification used by edge-layer; sync/async
   * pairs that share endpoints are treated as a single edge for diff purposes.
   */
  edges: Map<string, EdgeDiffMeta>;
}

/**
 * Compare-mode identity of an edge. Defined in `view/edge-key.ts` (#3111), so
 * view extraction can key its dedup sets on the same injective form without
 * importing from `diff/`; re-exported here because it is public API.
 */
export { edgeKey } from "../view/edge-key.js";

function nodeChanges(before: KrsNode, after: KrsNode): NodeDiffMeta["changes"] | undefined {
  const out: NonNullable<NodeDiffMeta["changes"]> = {};
  if ((before.label ?? "") !== (after.label ?? "")) {
    out.label = { before: before.label, after: after.label };
  }
  const beforeAnn = new Set(before.annotations);
  const afterAnn = new Set(after.annotations);
  const added = [...afterAnn].filter((a) => !beforeAnn.has(a));
  const removed = [...beforeAnn].filter((a) => !afterAnn.has(a));
  if (added.length > 0 || removed.length > 0) {
    out.annotations = { added, removed };
  }
  const beforeDesc = before.properties.description;
  const afterDesc = after.properties.description;
  if ((beforeDesc ?? "") !== (afterDesc ?? "")) {
    out.description = { before: beforeDesc, after: afterDesc };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The full path the layout stamps on a canvas card of `slice` (`LayoutNode.path`):
 * the canvas scope plus the card's id, with the expanded service in between for
 * a domain spliced in by in-place expansion (#1921 / #2917). The same rule as
 * layout's `canvasPathOf` and, on the multi-system root, the primary frame's
 * `framePathOf`, whose scope is the first system, or nothing for the "Unassigned"
 * pseudo-system (its children are top-level nodes).
 */
function canvasCardPathOf(slice: ViewSlice): (id: string) => string[] {
  const container = slice.containerNode;
  const scope =
    container === null ||
    (container.id === UNASSIGNED_SYSTEM_ID && slice.ancestorChain.length === 0)
      ? []
      : [...slice.ancestorChain.map((n) => n.id), container.id];
  const expandedParentOf = new Map<string, string>();
  for (const frame of slice.expandedFrames) {
    for (const memberId of frame.memberIds) expandedParentOf.set(memberId, frame.containerId);
  }
  return (id) => {
    const parent = expandedParentOf.get(id);
    return parent !== undefined ? [...scope, parent, id] : [...scope, id];
  };
}

/**
 * The full path the layout stamps on a card of a non-primary system frame on
 * the multi-system root: the system plus the child id, or the id alone in the
 * "Unassigned" pseudo-system (its children are top-level nodes). The same rule
 * as `layoutMultipleSystems`' `framePathOf`; in-place expansion is primary-only,
 * so no expanded service sits in between.
 */
function frameCardPathOf(systemId: string): (id: string) => string[] {
  return systemId === UNASSIGNED_SYSTEM_ID ? (id) => [id] : (id) => [systemId, id];
}

function diffNodeArray(
  before: readonly KrsNode[],
  after: readonly KrsNode[],
  /**
   * Where each node's state is recorded: by bare id, or, for canvas cards, by
   * the card path (the bare view is then derived from it, see
   * {@link diffSystemViewSlices}).
   */
  record: Map<string, NodeDiffMeta> | ((id: string, meta: NodeDiffMeta) => void),
): KrsNode[] {
  const beforeById = new Map(before.map((n) => [n.id, n]));
  const merged: KrsNode[] = [];
  const seen = new Set<string>();
  const set =
    typeof record === "function"
      ? record
      : (id: string, meta: NodeDiffMeta) => record.set(id, meta);

  // Iterate in `after` order first so the union slice presents the new structure.
  for (const node of after) {
    const prev = beforeById.get(node.id);
    if (prev === undefined) {
      set(node.id, { state: "added" });
      merged.push(node);
    } else {
      const changes = nodeChanges(prev, node);
      // Annotation-only changes are rendered as a badge diff (D-2 in the design
      // doc / Issue #738): the node body stays `unchanged` so figures with
      // frequent annotation churn remain readable, while `changes.annotations`
      // is still carried forward for the renderer and detail panel.
      const bodyChanged = changes !== undefined && (changes.label || changes.description);
      set(node.id, { state: bodyChanged ? "changed" : "unchanged", changes });
      merged.push(node);
    }
    seen.add(node.id);
  }
  // Append removed nodes preserving their original order.
  for (const node of before) {
    if (seen.has(node.id)) continue;
    set(node.id, { state: "removed" });
    merged.push(node);
  }
  return merged;
}

function diffEdgeArray(
  before: readonly KrsEdge[],
  after: readonly KrsEdge[],
  diff: Map<string, EdgeDiffMeta>,
): KrsEdge[] {
  const beforeByKey = new Map(before.map((e) => [edgeKey(e), e]));
  const merged: KrsEdge[] = [];
  const seen = new Set<string>();

  for (const edge of after) {
    const key = edgeKey(edge);
    diff.set(key, { state: beforeByKey.has(key) ? "unchanged" : "added" });
    merged.push(withoutCyclic(edge));
    seen.add(key);
  }
  for (const edge of before) {
    const key = edgeKey(edge);
    if (seen.has(key)) continue;
    diff.set(key, { state: "removed" });
    merged.push(withoutCyclic(edge));
  }
  return merged;
}

/**
 * A diff does not draw cycles (#2791): a removed edge would bring the before
 * side's verdict while diff reports only the after side's warnings, and the
 * cyclic red is the "removed" red. Only a flagged edge is copied. Layout looks
 * cross-system edges up by object identity (`crossSystemSource`), so copying
 * every edge would send them all to the unscoped fallback.
 */
function withoutCyclic(edge: KrsEdge): KrsEdge {
  if (!edge.cyclic) return edge;
  const { cyclic: _cyclic, ...rest } = edge;
  return rest;
}

/**
 * Identity of one constituent domain edge within an aggregated edge. Local to
 * this module and never read back as text, so JSON is enough to keep it
 * injective: a `${from}->${to}#${label}` join let a quoted id carrying `->`
 * or `#` stand for a different pair (#2819).
 */
function detailKey(d: DomainEdgeDetail): string {
  return JSON.stringify([d.fromDomainId, d.toDomainId, d.label ?? ""]);
}

/**
 * For each aggregated implicit edge present in both slices, set-diff the
 * constituent domain edges. Produces a union detail map annotated with
 * per-row diffState and, when the set differs, updates the owning edge's
 * diff meta to `changed` with a `changes.domainEdges` payload.
 */
function diffImplicitEdgeDetails(
  before: ReadonlyMap<string, DomainEdgeDetail[]>,
  after: ReadonlyMap<string, DomainEdgeDetail[]>,
  edgeDiff: Map<string, EdgeDiffMeta>,
  edges: readonly KrsEdge[],
): Map<string, DomainEdgeDetail[]> {
  const merged = new Map<string, DomainEdgeDetail[]>();
  const keys = new Set<string>([...before.keys(), ...after.keys()]);
  // The details map is keyed `edgeKindKey` (derived-edges' `groupKey`). Its
  // diff state lives under `edgeKey` of the same edge, so map one to the other
  // through the edges themselves: cutting the detail key at its first `#`
  // misreads an endpoint that carries `#` or `->` (#2819).
  const edgeDiffKeyOf = new Map<string, string>();
  for (const edge of edges) {
    edgeDiffKeyOf.set(edgeKindKey(edge), edgeKey(edge));
  }

  for (const key of keys) {
    const b = before.get(key);
    const a = after.get(key);
    const edgeDiffKey = edgeDiffKeyOf.get(key);

    if (b === undefined || a === undefined) {
      // Only one side has details. Leave the underlying edge's state as-is
      // (diffEdgeArray already classified it as added/removed). The detail
      // rows are preserved with the parent state so the panel can still list
      // them.
      const parentState = a === undefined ? "removed" : "added";
      const source = a ?? b!;
      merged.set(
        key,
        source.map((d) => ({ ...d, diffState: parentState })),
      );
      continue;
    }

    const beforeKeys = new Map(b.map((d) => [detailKey(d), d]));
    const afterKeys = new Map(a.map((d) => [detailKey(d), d]));
    const added: DomainEdgeDetail[] = [];
    const removed: DomainEdgeDetail[] = [];
    const rows: DomainEdgeDetail[] = [];

    for (const d of a) {
      if (beforeKeys.has(detailKey(d))) {
        rows.push({ ...d, diffState: "unchanged" });
      } else {
        const withState: DomainEdgeDetail = { ...d, diffState: "added" };
        rows.push(withState);
        added.push(d);
      }
    }
    for (const d of b) {
      if (!afterKeys.has(detailKey(d))) {
        const withState: DomainEdgeDetail = { ...d, diffState: "removed" };
        rows.push(withState);
        removed.push(d);
      }
    }
    merged.set(key, rows);

    if ((added.length > 0 || removed.length > 0) && edgeDiffKey !== undefined) {
      const meta = edgeDiff.get(edgeDiffKey);
      if (meta && meta.state === "unchanged") {
        edgeDiff.set(edgeDiffKey, {
          state: "changed",
          changes: { domainEdges: { added, removed } },
        });
      }
    }
  }

  return merged;
}

/**
 * Merge the per-system-frame edge sets of the two slices (#2756), frame by frame.
 *
 * Needed because the multi-system layout reads `systemEdges`, not `childEdges`:
 * without the merge, compare mode on a multi-system root would hand the layout
 * only the `after` frames, and every *removed* derived edge would vanish from the
 * drawing — the same silent drop this fix is closing, re-opened one mode over.
 *
 * A frame present on one side only is carried through as-is, so a system added or
 * deleted between the two revisions keeps its edges. `undefined` when neither
 * slice has frames, which is every view but the root one.
 */
/** Stand-in for the missing side of a frame that exists in only one revision. */
const EMPTY_DETAILS: ReadonlyMap<string, DomainEdgeDetail[]> = new Map();

function diffSystemFrames(
  before: ViewSlice,
  after: ViewSlice,
  edgeDiff: Map<string, EdgeDiffMeta>,
): ReadonlyMap<string, SystemFrameEdges> | undefined {
  const b = before.systemEdges;
  const a = after.systemEdges;
  if (!b && !a) return undefined;
  const merged = new Map<string, SystemFrameEdges>();
  for (const systemId of new Set([...(b?.keys() ?? []), ...(a?.keys() ?? [])])) {
    // Diff this frame into a map of its own, then fold it into the shared one.
    // The frame-scoped copy is what the layout reads: the shared map is keyed by
    // `${from}->${to}` with no system in it, so when two frames both hold an
    // `Api->Store` the second frame diffed overwrites the first and a removal in
    // one system reads back as `unchanged` (#2756). The shared map is still
    // filled, for the single-system path and the consumers keyed off it.
    //
    // A frame on one side only — a system added or deleted between the revisions
    // — diffs against an empty stand-in rather than being passed through. Passing
    // it through left its edges with no state of their own, so they fell back to
    // the shared map, and a system added beside one that already held the same
    // edge id rendered `unchanged` instead of `added`. Going through the same
    // helper answers it without a second way to build the same keys.
    const bf = b?.get(systemId);
    const af = a?.get(systemId);
    const frameDiff = new Map<string, EdgeDiffMeta>();
    const edges = diffEdgeArray(bf?.edges ?? [], af?.edges ?? [], frameDiff);
    const implicitEdgeDetails = diffImplicitEdgeDetails(
      bf?.implicitEdgeDetails ?? EMPTY_DETAILS,
      af?.implicitEdgeDetails ?? EMPTY_DETAILS,
      frameDiff,
      edges,
    );
    for (const [key, meta] of frameDiff) edgeDiff.set(key, meta);
    merged.set(systemId, {
      edges,
      implicitEdgeDetails,
      edgeDiffState: new Map([...frameDiff].map(([key, meta]) => [key, meta.state])),
    });
  }
  return merged;
}

/**
 * Produce a union ViewSlice of two system view slices plus per-element diff state.
 *
 * The merged slice contains every node and edge from either side, ordered with
 * the "after" elements first, so the existing layout engine lays out a single
 * graph in which unchanged elements have stable positions while added/removed
 * elements appear in the same neighborhood as their context.
 *
 * On the multi-system root every system frame diffs its own children, paired
 * with the same system on the other side by id and never by position (#3126,
 * TPL-3126): each slice's `childNodes` holds *its own* `systems[0]`'s children,
 * so pairing the two lists compared two different systems as soon as the first
 * system was added or removed. A frame on one side only diffs against an empty
 * counterpart, the way {@link diffSystemFrames} does for edges.
 *
 * `ranking` (`KrsFile.nodePathRanking`) decides which card answers a bare id
 * when cards in two frames share it (ADR-2934). Without it the tie falls to the
 * path's key text.
 *
 * Aggregated implicit edges, ghost domains/systems, and resource label maps
 * are taken from `after` for now — refining their diff semantics is tracked
 * separately (see ADR-650).
 */
export function diffSystemViewSlices(
  before: ViewSlice,
  after: ViewSlice,
  ranking: ReadonlyMap<string, readonly (readonly string[])[]> = new Map(),
): DiffedView {
  const nodeDiffByPath = new Map<string, NodeDiffMeta>();
  const edgeDiff = new Map<string, EdgeDiffMeta>();
  const recordCard =
    (pathOf: (id: string) => string[]) =>
    (id: string, meta: NodeDiffMeta): void => {
      nodeDiffByPath.set(nodePathRefId(pathOf(id)), meta);
    };

  // Systems first, so each frame's pair is known. Their bare states are written
  // after the cards' bare view below, as they always were.
  const systemDiff = new Map<string, NodeDiffMeta>();
  const unionSystems = diffNodeArray(before.systems, after.systems, systemDiff);
  const beforeSystemById = new Map(before.systems.map((s) => [s.id, s]));
  const afterSystemById = new Map(after.systems.map((s) => [s.id, s]));

  // The primary frame stays in `childNodes`, which carries in-place expansion
  // (#1921). Its before side is the same system's: `before.childNodes` only when
  // before's first system is that system too. Keyed by the path the layout will
  // stamp on each card of the union slice, whose canvas scope and expanded
  // frames are the after side's (see `slice` below), so a removed card is keyed
  // the way it is drawn.
  const primary = unionSystems[0];
  const beforePrimaryChildren =
    primary === undefined || before.systems[0]?.id === primary.id
      ? before.childNodes
      : (beforeSystemById.get(primary.id)?.children ?? []);
  // When after has no system at all (the whole model was deleted), the primary
  // is a before-only system and after's canvas scope says nothing about it. The
  // multi-system layout then scopes the frame by the system, as it does every
  // frame; a lone system goes through the single-canvas layout, which scopes it
  // by after's (empty) container like any other card.
  const primaryPathOf =
    primary !== undefined && after.systems.length === 0 && unionSystems.length > 1
      ? frameCardPathOf(primary.id)
      : canvasCardPathOf(after);
  const childNodes = diffNodeArray(
    beforePrimaryChildren,
    after.childNodes,
    recordCard(primaryPathOf),
  );
  // Every other frame: the layout places `sys.children`, so the union system
  // node carries the union of the frame's children, removed ones included.
  const systems = unionSystems.map((sys, i) => {
    if (i === 0) return sys;
    const children = diffNodeArray(
      beforeSystemById.get(sys.id)?.children ?? [],
      afterSystemById.get(sys.id)?.children ?? [],
      recordCard(frameCardPathOf(sys.id)),
    );
    return { ...sys, children };
  });

  const nodeDiff = bareIdView(nodeDiffByPath, ranking);
  for (const [id, meta] of systemDiff) nodeDiff.set(id, meta);
  const childEdges = diffEdgeArray(before.childEdges, after.childEdges, edgeDiff);
  const crossSystemEdges = diffEdgeArray(before.crossSystemEdges, after.crossSystemEdges, edgeDiff);
  const ghostUsers = diffNodeArray(before.ghostUsers, after.ghostUsers, nodeDiff);
  const ghostUserEdges = diffEdgeArray(before.ghostUserEdges, after.ghostUserEdges, edgeDiff);

  const slice: ViewSlice = {
    containerNode: after.containerNode,
    childNodes,
    childEdges,
    ancestorChain: after.ancestorChain,
    ghostUsers,
    ghostUserEdges,
    systems,
    crossSystemEdges,
    // A union: a cross-system edge kept from `before` (a removal) still needs
    // its target path to anchor on, and `after` wins on a key both sides carry.
    crossSystemTargets: new Map([...before.crossSystemTargets, ...after.crossSystemTargets]),
    ghostSystems: after.ghostSystems,
    ghostSystemEdges: after.ghostSystemEdges,
    callerGhostSystems: after.callerGhostSystems,
    callerGhostSystemEdges: after.callerGhostSystemEdges,
    ghostDomains: after.ghostDomains,
    ghostDomainEdges: after.ghostDomainEdges,
    ghostEntities: after.ghostEntities,
    ghostEntityEdges: after.ghostEntityEdges,
    resourceLabelMap: after.resourceLabelMap,
    resourceInferredTagsMap: after.resourceInferredTagsMap,
    implicitEdgeDetails: diffImplicitEdgeDetails(
      before.implicitEdgeDetails,
      after.implicitEdgeDetails,
      edgeDiff,
      [...childEdges, ...crossSystemEdges, ...ghostUserEdges],
    ),
    systemEdges: diffSystemFrames(before, after, edgeDiff),
    expandedFrames: after.expandedFrames,
  };

  return { slice, nodes: nodeDiff, nodesByPath: nodeDiffByPath, edges: edgeDiff };
}
